import { useState } from 'react';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import useAsyncEffect from 'use-async-effect';
import { GroupedOtherBets } from 'components/OtherBets/GroupedOtherBets';
import { Loader } from 'components/Loader/Loader';
import { getEventByid } from 'services/api/sportBetsService';
import { getAllBets } from 'helpers/matchHelper';
import { formatDateTime } from 'helpers/dateHelper';
import { MatchOffer } from 'types/MatchOffer';
import styles from './MatchOfferModal.module.css';

interface Props {
  fi: number | string;
  onClose: () => void;
}

/**
 * A prematch event's whole offer, opened from the slip, so a pick can be
 * swapped (1 for 3+ goals, say) without going back to find the match.
 * Clicking a price adds or removes it on the slip as anywhere else.
 */
export const MatchOfferModal = ({ fi, onClose }: Props) => {
  const { t } = useTranslation();
  const [match, setMatch] = useState<MatchOffer | null | undefined>(undefined);

  useAsyncEffect(async () => {
    setMatch((await getEventByid(Number(fi))) ?? null);
  }, [fi]);

  const kickoff = match?.time
    ? formatDateTime(new Date(Number(match.time) * 1000), {
        removeSeconds: true,
        removeYear: true,
      })
    : '';

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      onRequestClose={onClose}
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <div className={styles.header}>
        <div className={styles.title}>
          {match ? (
            <>
              <span className={styles.teams}>
                {match.home?.name} – {match.away?.name}
              </span>
              <span className={styles.meta}>
                {kickoff}
                {match.league?.name ? ` · ${match.league.name}` : ''}
              </span>
            </>
          ) : (
            <span className={styles.teams}>{t('loading_label')}</span>
          )}
        </div>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className={styles.body}>
        {match === undefined && <Loader inline />}
        {match === null && (
          <p className={styles.empty}>{t('no_bet_offer_message')}</p>
        )}
        {match && (
          <GroupedOtherBets bets={getAllBets(match, true)} match={match} />
        )}
      </div>
    </Modal>
  );
};
